import {
  Button,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  Icon,
  Select,
  useToast,
} from "@chakra-ui/react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { FaSave } from "react-icons/fa";
import { OrganizationSize } from "../../../../autogen/bff-api";
import { useApiError } from "../../../../common/errors/useApiError";
import { displayOrganizationSize } from "../../../../common/formatting/displayOrganizationSize";
import { useSaveOrganization } from "../useOrganizationEditor";

interface FormValues {
  size: OrganizationSize | null;
}

interface Props {
  currentValue: OrganizationSize | null;
}

export const ChangeSize = ({ currentValue }: Props) => {
  const { saveOrganization, isSaving } = useSaveOrganization();
  const toast = useToast();
  const { t } = useTranslation();
  const displayer = useApiError();
  const {
    formState: { errors },
    register,
    handleSubmit,
    watch,
  } = useForm<FormValues>({
    defaultValues: {
      size: currentValue,
    },
  });

  let newValue: string | OrganizationSize | null = watch("size");
  if (newValue === "") {
    newValue = null;
  }

  const onSubmit = async (values: FormValues) => {
    const isNull: boolean = values.size !== null && values.size.toString() === "";

    try {
      await saveOrganization({
        size: {
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          size: isNull ? undefined : values.size!,
        },
      });
      toast({ title: t("Company size has been updated!"), status: "success" });
    } catch (e) {
      displayer.trigger(e);
    }
  };
  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)}>
        <FormControl id="size" mt="20px" isInvalid={errors.size != null}>
          <FormLabel>{t("Company size")}</FormLabel>
          <Select placeholder={t("Select a size for your company") ?? ""} {...register("size")}>
            {organizationSizeToList().map((e) => (
              <option key={e} value={e}>
                {displayOrganizationSize(e)}
              </option>
            ))}
          </Select>
          <FormHelperText>{t("Please provide a size for your company")}.</FormHelperText>
          <FormErrorMessage>{errors.size && errors.size.message}</FormErrorMessage>
        </FormControl>
        {currentValue !== newValue && (
          <Button
            mt="10px"
            leftIcon={<Icon as={FaSave} w="15px" h="15px" mt="-2px" />}
            variant={"solid"}
            colorScheme="teal"
            size={"sm"}
            type="submit"
            isLoading={isSaving}
          >
            {t("Save change")}
          </Button>
        )}
      </form>
    </>
  );
};

const organizationSizeToList = (): OrganizationSize[] => {
  return [
    OrganizationSize.OneToTen,
    OrganizationSize.TenToFifty,
    OrganizationSize.FiftyToTwoHundredAndFifty,
    OrganizationSize.TwoHundredAndFiftyToThousand,
    OrganizationSize.ThousandToTenThousand,
    OrganizationSize.TenThousandPlus,
  ];
};
